"use client";

import React from "react";
import { Footer } from "@/components/Footer";
import {
  SparklesIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  ClockIcon,
  LightBulbIcon,
  HandRaisedIcon,
  DevicePhoneMobileIcon,
  ChatBubbleLeftRightIcon,
  CreditCardIcon,
  TrophyIcon,
  VideoCameraIcon,
  BellAlertIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

const AboutUsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white mt-[60px] sm:mt-[80px]">
      {/* Hero Header Section */}
      <section className="relative w-full py-12 sm:py-16 md:py-20 bg-white overflow-hidden zoom-container">
        {/* Subtle Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
          <div className="absolute top-[-20%] left-[-10%] w-[300px] sm:w-[400px] h-[300px] sm:h-[400px] bg-gradient-to-r from-[#ff7043]/10 to-purple-500/10 rounded-full mix-blend-multiply filter blur-3xl" />
          <div className="absolute bottom-[-20%] right-[-10%] w-[250px] sm:w-[350px] h-[250px] sm:h-[350px] bg-gradient-to-r from-blue-500/10 to-[#463a33]/10 rounded-full mix-blend-multiply filter blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm rounded-full px-4 sm:px-5 py-2 sm:py-3 mb-6 sm:mb-8 border border-gray-200 shadow-lg">
              <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
              <span className="text-gray-700 text-xs sm:text-sm font-medium">
                Powered by Prana AI
              </span>
            </div>

            <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-[#463a33] mb-2 sm:mb-3 font-sans tracking-tight leading-tight">
              Welcome to
              <span className="text-[#ff7043] font-medium ml-2">
                Relaxing Routine
              </span>
            </h1>

            <div className="w-12 sm:w-16 h-0.5 bg-[#ff7043] mx-auto mb-4 sm:mb-6 opacity-80"></div>

            <div className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed font-normal max-w-4xl mx-auto space-y-6 text-center">
              <p>
                Relaxing Routine is <b>a wellness marketplace</b> designed to help you build healthier, more balanced routines through guided sessions and personalized support.
              </p>
              <p>
                We connect you with <b>experienced wellness instructors</b> offering live and on-demand classes across yoga, meditation, breathwork, fitness, and holistic wellbeing. Alongside expert-led sessions, <b>Prana AI</b> helps you discover classes that match your goals, preferences, and lifestyle — so you can progress at your own pace, on your own terms.
              </p>
              <p>
                Whether you’re starting your wellness journey or looking to stay consistent, <b>Relaxing Routine</b> gives you the structure, flexibility, and guidance to support your everyday wellbeing.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
                <a
                  href="/classes"
                  className="group relative px-6 py-3 bg-gradient-to-r from-[#ff7043] to-[#ff5722] text-white font-bold tracking-wide rounded-xl shadow-[0_4px_10px_-4px_rgba(255,112,67,0.5)] hover:shadow-[0_8px_16px_-4px_rgba(255,112,67,0.6)] hover:-translate-y-0.5 transition-all duration-300 w-full sm:w-auto text-center flex items-center justify-center gap-2 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 rounded-xl"></div>
                  <MagnifyingGlassIcon className="w-5 h-5 stroke-[2.5] relative z-10 group-hover:scale-110 transition-transform" />
                  <span className="relative z-10 text-sm sm:text-base">Browse Classes</span>
                </a>
                <a
                  href="/ai-assistant"
                  className="group relative px-6 py-3 bg-white text-[#ff7043] font-bold tracking-wide border-2 border-[#ff7043]/20 hover:border-[#ff7043] rounded-xl shadow-[0_4px_10px_-4px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_16px_-4px_rgba(255,112,67,0.15)] hover:-translate-y-0.5 transition-all duration-300 w-full sm:w-auto text-center flex items-center justify-center gap-2"
                >
                  <SparklesIcon className="w-5 h-5 stroke-[2.5] group-hover:rotate-12 transition-transform text-[#ff7043]" />
                  <span className="text-sm sm:text-base">Talk to Prana AI</span>
                </a>
              </div>


            </div>
          </div>
        </div>
      </section>



      {/* Mission Section */}
      <section className="w-full py-12 sm:py-16 md:py-20 bg-white zoom-container">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-[#463a33] mb-2 sm:mb-3 font-sans tracking-tight leading-tight">
              Our
              <span className="text-[#ff7043] font-medium ml-2">Mission</span>
            </h2>
            <div className="w-12 sm:w-16 h-0.5 bg-[#ff7043] mx-auto mb-4 sm:mb-6 opacity-80"></div>
            <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed font-normal max-w-3xl mx-auto">
              To empower people to reduce stress, improve their health, and build
              meaningful wellness habits through guided programs, experienced instructors, and personalized Prana AI recommendations.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 items-stretch">
              {/* Problem Card */}
              <div className="bg-gradient-to-br from-red-50 to-red-100/50 p-6 rounded-2xl border border-red-200/50">
                <div className="mb-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-500 rounded-xl flex items-center justify-center mb-3">
                    <span className="text-white font-bold text-lg">!</span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                    The Problem
                  </h3>
                  <p className="font-medium text-gray-900 text-base mb-4">
                    Life today is stressful. People struggle with:
                  </p>
                </div>

                <ul className="space-y-4">
                  {[
                    "Stress, anxiety, and burnout",
                    "Busy schedules with little time for self-care",
                    "Confusing and overwhelming fitness information",
                    "Lack of proper guidance, structure, or consistency",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-700 text-sm sm:text-base">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 text-red-500 flex items-center justify-center text-xs mt-0.5 border border-red-200">
                        ✕
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Solution Card */}
              <div className="bg-gradient-to-br from-green-50 to-green-100/50 p-6 rounded-2xl border border-green-200/50">
                <div className="mb-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500 rounded-xl flex items-center justify-center mb-3">
                    <span className="text-white font-bold text-lg">✓</span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                    Our Solution
                  </h3>
                  <h4 className="font-bold text-[#463a33] mb-2 text-base">
                    Relaxing Routine was created to change that.
                  </h4>
                </div>


                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-6">
                  We bring everything together in one supportive, structured
                  wellness platform—helping you stay motivated, supported, and
                  truly connected to your health journey.
                </p>

                <div className="flex items-center gap-3 px-4 py-3 bg-white/60 rounded-lg text-[#ff7043] font-medium border border-orange-100/50 w-fit">
                  <span className="w-2 h-2 rounded-full bg-[#ff7043]"></span>
                  Your platform for guided wellness routines
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full py-12 sm:py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 zoom-container">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-[#463a33] mb-2 sm:mb-3 font-sans tracking-tight leading-tight">
              How It
              <span className="text-[#ff7043] font-medium ml-2">Works</span>
            </h2>
            <div className="w-12 sm:w-16 h-0.5 bg-[#ff7043] mx-auto mb-4 sm:mb-6 opacity-80"></div>
            <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed font-normal max-w-2xl mx-auto">
              Getting started with your wellness journey is simple. Follow these
              easy steps to connect with the right expert for you.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Step 1 */}
              <div className="relative">
                <div className="bg-white p-6 rounded-xl border border-gray-200 hover:border-[#ff7043] transition-colors h-full">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">
                    1
                  </div>
                  <h3 className="text-base font-semibold text-[#463a33] mb-2">
                    Browse Experts
                  </h3>
                  <p className="text-sm text-gray-600">
                    Explore our curated list of experienced wellness instructors
                    across yoga, meditation, fitness, nutrition, and more
                  </p>
                </div>

                <div className="hidden lg:block absolute top-6 -right-3 w-6 h-0.5 bg-gradient-to-r from-orange-300 to-transparent"></div>
              </div>

              <div className="relative">
                <div className="bg-white p-6 rounded-xl border border-gray-200 hover:border-[#ff7043] transition-colors h-full">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">
                    2
                  </div>
                  <h3 className="text-base font-semibold text-[#463a33] mb-2">
                    Check Profiles
                  </h3>
                  <p className="text-sm text-gray-600">
                    Review instructor profiles, specializations, and ratings.
                    Our system helps you find instructors aligned with your goals.
                  </p>
                </div>
                <div className="hidden lg:block absolute top-6 -right-3 w-6 h-0.5 bg-gradient-to-r from-blue-300 to-transparent"></div>
              </div>

              <div className="relative">
                <div className="bg-white p-6 rounded-xl border border-gray-200 hover:border-[#ff7043] transition-colors h-full">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">
                    3
                  </div>
                  <h3 className="text-base font-semibold text-[#463a33] mb-2">
                    Book Classes
                  </h3>
                  <p className="text-sm text-gray-600">
                    Choose from live sessions, on-demand classes, or private
                    consultations. Book instantly with secure payments
                  </p>
                </div>
                <div className="hidden lg:block absolute top-6 -right-3 w-6 h-0.5 bg-gradient-to-r from-green-300 to-transparent"></div>
              </div>

              <div>
                <div className="bg-white p-6 rounded-xl border border-gray-200 hover:border-[#ff7043] transition-colors h-full">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">
                    4
                  </div>
                  <h3 className="text-base font-semibold text-[#463a33] mb-2">
                    Start Learning
                  </h3>
                  <p className="text-sm text-gray-600">
                    Join your classes, track your progress, earn achievements,
                    and transform your wellness journey
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 zoom-container">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-[#463a33] mb-2 sm:mb-3 font-sans tracking-tight leading-tight">
              Our Core
              <span className="text-[#ff7043] font-medium ml-2">Values</span>
            </h2>
            <div className="w-12 sm:w-16 h-0.5 bg-[#ff7043] mx-auto mb-4 sm:mb-6 opacity-80"></div>
            <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed font-normal max-w-2xl mx-auto">
              These principles guide everything we do and help us build a
              platform you can trust.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <div className="text-center p-6 bg-gradient-to-br from-orange-50 to-white rounded-xl border border-gray-100">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center text-white mx-auto mb-4">
                <ShieldCheckIcon className="w-7 h-7" />
              </div>
              <h3 className="text-base font-semibold text-[#463a33] mb-2">
                Trust & Safety
              </h3>
              <p className="text-sm text-gray-600">
                Instructors complete profile verification to help build trust
              </p>
            </div>

            <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-gray-100">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center text-white mx-auto mb-4">
                <SparklesIcon className="w-7 h-7" />
              </div>
              <h3 className="text-base font-semibold text-[#463a33] mb-2">
                Quality First
              </h3>
              <p className="text-sm text-gray-600">
                We curate only the best experts who demonstrate excellence in
                their field
              </p>
            </div>

            <div className="text-center p-6 bg-gradient-to-br from-green-50 to-white rounded-xl border border-gray-100">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center text-white mx-auto mb-4">
                <UserGroupIcon className="w-7 h-7" />
              </div>
              <h3 className="text-base font-semibold text-[#463a33] mb-2">
                Community Driven
              </h3>
              <p className="text-sm text-gray-600">
                Building a supportive community where everyone can share, learn,
                and grow together
              </p>
            </div>

            <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-white rounded-xl border border-gray-100">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center text-white mx-auto mb-4">
                <DevicePhoneMobileIcon className="w-7 h-7" />
              </div>
              <h3 className="text-base font-semibold text-[#463a33] mb-2">
                Innovation
              </h3>
              <p className="text-sm text-gray-600">
                Leveraging modern technology to make wellness accessible,
                convenient, and effective
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full py-12 sm:py-16 md:py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 zoom-container">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-[#463a33] mb-2 sm:mb-3 font-sans tracking-tight leading-tight">
              What Makes Us
              <span className="text-[#ff7043] font-medium ml-2">Different</span>
            </h2>
            <div className="w-12 sm:w-16 h-0.5 bg-[#ff7043] mx-auto mb-4 sm:mb-6 opacity-80"></div>
            <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed font-normal max-w-2xl mx-auto">
              Modern technology meets traditional wellness. Here&apos;s what you
              get with our platform.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-6xl mx-auto">
            <div className="bg-white p-4 sm:p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center text-white mb-3 sm:mb-4">
                <ChatBubbleLeftRightIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <h3 className="font-semibold text-sm sm:text-base text-[#463a33] mb-2">
                Connect with instructors for session coordination
              </h3>
              <p className="text-xs sm:text-sm text-gray-600">
                Real-time messaging to ask questions, share progress, and get
                guidance anytime
              </p>
            </div>

            <div className="bg-white p-4 sm:p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white mb-3 sm:mb-4">
                <TrophyIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <h3 className="font-semibold text-sm sm:text-base text-[#463a33] mb-2">
                Prana AI Recommendations
              </h3>
              <p className="text-xs sm:text-sm text-gray-600">
                Get personalized workout recommendations and track your growth
                with Prana, your intelligent wellness guide.
              </p>
            </div>

            <div className="bg-white p-4 sm:p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center text-white mb-3 sm:mb-4">
                <CreditCardIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <h3 className="font-semibold text-sm sm:text-base text-[#463a33] mb-2">
                Safe & Secure Payments
              </h3>
              <p className="text-xs sm:text-sm text-gray-600">
                PayU integration ensures your payments are processed
                securely every time
              </p>
            </div>

            <div className="bg-white p-4 sm:p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center text-white mb-3 sm:mb-4">
                <VideoCameraIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <h3 className="font-semibold text-sm sm:text-base text-[#463a33] mb-2">
                High-Quality Video
              </h3>
              <p className="text-xs sm:text-sm text-gray-600">
                Crystal-clear live streaming for online classes so you
                don&apos;t miss any detail
              </p>
            </div>

            <div className="bg-white p-4 sm:p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center text-white mb-3 sm:mb-4">
                <BellAlertIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <h3 className="font-semibold text-sm sm:text-base text-[#463a33] mb-2">
                Get reminders for upcoming classes
              </h3>
              <p className="text-xs sm:text-sm text-gray-600">
                Get reminders via email or SMS before your classes start
              </p>
            </div>

            <div className="bg-white p-4 sm:p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-pink-500 to-rose-500 rounded-lg flex items-center justify-center text-white mb-3 sm:mb-4">
                <UserGroupIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <h3 className="font-semibold text-sm sm:text-base text-[#463a33] mb-2">
                Join the Community
              </h3>
              <p className="text-xs sm:text-sm text-gray-600">
                Connect with other wellness enthusiasts, share experiences, and
                stay motivated
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full py-12 sm:py-16 md:py-20  bg-white zoom-container">
        <div className="container mx-auto px-4 zoom-container">
          <div className="text-center mb-12 sm:mb-16">
            <span className="inline-block px-3 sm:px-4 py-2 bg-orange-500/15 text-orange-500 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              Wellness Ecosystem
            </span>
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
              Comprehensive Wellness Disciplines
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
              From ancient practices to modern wellness coaching, our growing
              team of instructor profiles reviewed on the platform is ready to guide every aspect of your
              holistic health journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12">
            <div className="bg-gradient-to-br from-orange-50 to-orange-100/50 p-4 sm:p-6 rounded-2xl border border-orange-200/50">
              <div className="mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-500 rounded-xl flex items-center justify-center mb-3">
                  <SparklesIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                  Traditional Practices
                </h3>
              </div>
              <ul className="space-y-2 text-gray-700 text-sm sm:text-base">
                <li className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-3"></span>
                  Yoga Instruction
                </li>
                <li className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-3"></span>
                  Pranayama Coaching
                </li>
                <li className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-3"></span>
                  Meditation Guidance
                </li>
                <li className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-3"></span>
                  Ayurveda Consultation
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 p-4 sm:p-6 rounded-2xl border border-blue-200/50">
              <div className="mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-3">
                  <LightBulbIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                  Modern Wellness
                </h3>
              </div>
              <ul className="space-y-2 text-gray-700 text-sm sm:text-base">
                <li className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-3"></span>
                  Nutrition Guidance
                </li>
                <li className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-3"></span>
                  Fitness Training
                </li>
                <li className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-3"></span>
                  Zumba Instruction
                </li>
                <li className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-3"></span>
                  Self Defense Training
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 p-4 sm:p-6 rounded-2xl border border-purple-200/50">
              <div className="mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-500 rounded-xl flex items-center justify-center mb-3">
                  <HandRaisedIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                  Specialized Coaching
                </h3>
              </div>
              <ul className="space-y-2 text-gray-700 text-sm sm:text-base">
                <li className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-3"></span>
                  Women&apos;s Wellness
                </li>
                <li className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-3"></span>
                  Self Defense
                </li>
              </ul>
            </div>
          </div>


        </div>
      </section>

      <Footer />

      <style jsx global>{`
        @keyframes blob {
          0%,
          100% {
            transform: translate(0, 0) scale(1);
          }
          25% {
            transform: translate(20px, -50px) scale(1.1);
          }
          50% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          75% {
            transform: translate(50px, 50px) scale(1.05);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-blob {
          animation: blob 7s infinite;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }

        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out forwards;
          opacity: 0;
        }

        .animate-fade-in-delayed {
          animation: fadeIn 0.8s ease-out 0.3s forwards;
          opacity: 0;
        }

        .animate-fade-in-delayed-more {
          animation: fadeIn 0.8s ease-out 0.6s forwards;
          opacity: 0;
        }

        .animate-slide-in-left {
          animation: slideInLeft 0.8s ease-out forwards;
          opacity: 0;
        }

        .animate-slide-in-right {
          animation: slideInRight 0.8s ease-out forwards;
          opacity: 0;
        }

        .animate-fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default AboutUsPage;
